import { useState, useEffect, useCallback, useMemo } from "react";
import {
  DeliveryMethod,
  useShoppingCart,
} from "../context/ShoppingCartContext";
import CheckoutPaymentMethodButton from "./CheckoutPaymentMethodButton";
import WestIcon from "@mui/icons-material/West";
import CheckoutConfirmModal from "./CheckoutConfirmModal";
import CheckoutSelectDeliveryMethodModal from "./CheckoutSelectDeliveryMethodModal";
import CheckoutSelectPaymentMethodModal from "./CheckoutSelectPaymentMethodModal";
import CheckoutErrorModal from "./CheckoutErrorModal";
import usePageLanguage from "../hooks/usePageLanguage";
import React from "react";
import {
  filterDeliveryMethodsForConfig,
  getCheckoutStepNumbers,
  getConfirmOrderButtonText,
  getPaymentStepNumber,
  getSyncOrderCheckoutContext,
  isDeliveryDisabledByFixedNone,
  isOrderDeliverySelectionComplete,
  resolveSelectedPaymentMethod,
  useCheckoutConfig,
} from "./checkoutConfig";

// Define the steps that need to be completed in order
type CheckoutStep = "buyer" | "delivery" | "payment" | "confirm";

interface Props {
  setShowBuyerModal: (val: boolean) => void;
  setModalDeliveryMethod: (val: DeliveryMethod | null) => void;
  setShowDeliveryModal: (val: boolean) => void;
  showDeliveryModal?: boolean; // Track if delivery details modal is open
  showBuyerModal?: boolean; // Track if buyer modal is open
  pendingSteps?: CheckoutStep[];
  setPendingSteps?: (steps: CheckoutStep[]) => void;
  onBuyerModalClose?: () => void; // Called when buyer modal is closed by user
  readOnly?: boolean;
  showPaymentBlock?: boolean;
}

const languageTranslations = {
  et: {
    payment_method: "MAKSE",
    back_to_payment: "Tagasi makseviisi valikusse",
    confirm_order:
      "Kohe pärast tellimuse kinnitamist ja tingimustega nõustumist suunatakse edasi internetipanka makset sooritama.",
  },
  en: {
    payment_method: "PAYMENT",
    back_to_payment: "Back to the payment methods",
    confirm_order:
      "Immediately after confirming the order and accepting the terms, you will be forwarded to the online bank to make the payment.",
  },
};

export default function CheckoutPaymentPanel({
  setShowBuyerModal,
  setModalDeliveryMethod,
  setShowDeliveryModal,
  showDeliveryModal = false,
  showBuyerModal = false,
  pendingSteps = [],
  setPendingSteps = () => {},
  onBuyerModalClose,
  readOnly = false,
  showPaymentBlock = true,
}: Props) {
  const [showCheckoutConfirmModal, setShowCheckoutConfirmModal] =
    useState(false);
  const [showSelectDeliveryMethodModal, setShowSelectDeliveryMethodModal] =
    useState(false);
  const [showSelectPaymentMethodModal, setShowSelectPaymentMethodModal] =
    useState(false);
  const [isPreparingConfirmation, setIsPreparingConfirmation] = useState(false);
  const [showPrepareOrderError, setShowPrepareOrderError] = useState(false);
  const [prepareOrderErrorMessage, setPrepareOrderErrorMessage] = useState<
    string | undefined
  >(undefined);

  // Track if we're waiting for delivery API response (use state to trigger re-renders)
  const [isProcessingDelivery, setIsProcessingDelivery] = useState(false);
  // Track if we're waiting for payment selection
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  const {
    paymentMethods,
    paymentMethod,
    order,
    deliveryMethods,
    setPaymentMethod,
    syncOrder,
    buyer,
    showCheckoutErrorModal,
    checkoutErrorMessage,
    handleCheckoutErrorModalClose,
  } = useShoppingCart();
  const checkoutConfig = useCheckoutConfig();
  const checkoutContext = getSyncOrderCheckoutContext(checkoutConfig.delivery);
  const stepNumbers = getCheckoutStepNumbers(checkoutConfig);
  const requiresDeliverySelection = !isDeliveryDisabledByFixedNone(
    checkoutConfig.delivery
  );
  const requiresPaymentSelection = true;
  const paymentStepNumber =
    stepNumbers.payment ?? getPaymentStepNumber(checkoutConfig);

  const paymentMethodForDisplay = useMemo(() => {
    const validPaymentMethod = resolveSelectedPaymentMethod(
      paymentMethod,
      paymentMethods
    );
    if (validPaymentMethod?.uuid) {
      return validPaymentMethod;
    }
    if (readOnly && paymentMethods?.length === 1 && paymentMethods[0]?.uuid) {
      return paymentMethods[0];
    }
    return null;
  }, [paymentMethod, paymentMethods, readOnly]);

  const locale = usePageLanguage();
  const visibleDeliveryMethods = useMemo(
    () => filterDeliveryMethodsForConfig(deliveryMethods, checkoutConfig.delivery),
    [deliveryMethods, checkoutConfig.delivery]
  );
  const selectedDeliveryMethod = useMemo(() => {
    if (!order?.delivery?.uuid) return null;
    return (
      visibleDeliveryMethods.find(
        (method) => method.uuid === order.delivery?.uuid
      ) ?? null
    );
  }, [visibleDeliveryMethods, order?.delivery?.uuid]);

  const isDeliveryComplete = useCallback(() => {
    return isOrderDeliverySelectionComplete(
      order?.delivery ?? null,
      selectedDeliveryMethod
    );
  }, [order?.delivery, selectedDeliveryMethod]);

  const isPaymentComplete = useCallback(() => {
    return !!resolveSelectedPaymentMethod(paymentMethod, paymentMethods)?.uuid;
  }, [paymentMethod, paymentMethods]);

  // Helper function to check if buyer info is complete
  const isBuyerComplete = useCallback(() => {
    if (!buyer) return false;
    if (buyer?.type !== "organization" && !buyer.firstname) return false;
    if (buyer?.type === "organization" && !buyer.organization_name)
      return false;
    if (
      buyer?.type === "organization" &&
      checkoutConfig.b2bContactPerson.visible &&
      checkoutConfig.b2bContactPerson.required &&
      (!buyer.representativeFirstname?.trim() ||
        !buyer.representativeSurname?.trim())
    ) {
      return false;
    }
    return true;
  }, [buyer, checkoutConfig.b2bContactPerson]);

  // Check if any modal is currently open
  const isAnyModalOpen = useCallback(() => {
    return (
      showBuyerModal ||
      showDeliveryModal ||
      showSelectDeliveryMethodModal ||
      showSelectPaymentMethodModal ||
      showCheckoutConfirmModal
    );
  }, [
    showBuyerModal,
    showDeliveryModal,
    showSelectDeliveryMethodModal,
    showSelectPaymentMethodModal,
    showCheckoutConfirmModal,
  ]);

  // Cancel the sequential flow (called when user closes a modal)
  const cancelFlow = useCallback(() => {
    setPendingSteps([]);
    setIsProcessingDelivery(false);
    setIsProcessingPayment(false);
  }, [setPendingSteps]);

  // Handle delivery modal open - track that we're waiting for API
  const handleDeliveryModalOpen = useCallback(() => {
    setIsProcessingDelivery(true);
  }, []);

  // Clear delivery processing when delivery is actually set
  useEffect(() => {
    if (order?.delivery?.uuid) {
      setIsProcessingDelivery(false);
    }
  }, [order?.delivery?.uuid]);

  // Clear payment processing when a valid payment method is selected
  useEffect(() => {
    if (isPaymentComplete()) {
      setIsProcessingPayment(false);
    }
  }, [paymentMethod, paymentMethods, isPaymentComplete]);

  // Auto-select payment method when there is exactly 1 and it has is_autoset_activated
  useEffect(() => {
    if (readOnly) return;
    if (
      paymentMethods?.length === 1 &&
      paymentMethods[0]?.isAutosetActivated &&
      !resolveSelectedPaymentMethod(paymentMethod, paymentMethods)?.uuid
    ) {
      setPaymentMethod(paymentMethods[0]);
    }
  }, [paymentMethods, paymentMethod, setPaymentMethod, readOnly]);

  // Process pending steps - show the next modal when conditions are met
  useEffect(() => {
    if (readOnly) return;

    if (pendingSteps.length === 0) return;

    // Don't process if any modal is already open
    if (isAnyModalOpen()) {
      return;
    }

    // Don't process if we're waiting for an API response
    if (isProcessingDelivery || isProcessingPayment) {
      return;
    }

    const nextStep = pendingSteps[0];

    // Check if the current step is now complete, move to next
    if (nextStep === "buyer" && isBuyerComplete()) {
      // Buyer step is complete, remove it and process next
      const remainingSteps = pendingSteps.slice(1);
      setPendingSteps(remainingSteps);
      return;
    }

    if (
      nextStep === "delivery" &&
      (!requiresDeliverySelection || isDeliveryComplete())
    ) {
      // Delivery step is complete, remove it and process next
      const remainingSteps = pendingSteps.slice(1);
      setPendingSteps(remainingSteps);
      return;
    }

    if (
      nextStep === "payment" &&
      (!requiresPaymentSelection || isPaymentComplete())
    ) {
      // Payment step is complete, remove it and process next
      const remainingSteps = pendingSteps.slice(1);
      setPendingSteps(remainingSteps);
      return;
    }

    // Handle confirm step - check if we should skip to it or show it
    if (nextStep === "confirm") {
      // All prerequisite steps are complete, show confirm modal
      setPendingSteps([]);
      setShowCheckoutConfirmModal(true);
      return;
    }

    // Show the appropriate modal for the current step
    if (nextStep === "buyer" && !isBuyerComplete()) {
      setShowBuyerModal(true);
    } else if (
      nextStep === "delivery" &&
      requiresDeliverySelection &&
      !isDeliveryComplete()
    ) {
      if (!selectedDeliveryMethod) {
        setShowSelectDeliveryMethodModal(true);
      } else {
        setModalDeliveryMethod(selectedDeliveryMethod);
        setShowDeliveryModal(true);
        handleDeliveryModalOpen();
      }
    } else if (
      nextStep === "payment" &&
      requiresPaymentSelection &&
      !isPaymentComplete()
    ) {
      setShowSelectPaymentMethodModal(true);
    }
  }, [
    pendingSteps,
    buyer,
    order?.delivery?.uuid,
    paymentMethod,
    requiresDeliverySelection,
    requiresPaymentSelection,
    isAnyModalOpen,
    isBuyerComplete,
    setPendingSteps,
    setShowBuyerModal,
    isProcessingDelivery,
    isProcessingPayment,
    readOnly,
    isDeliveryComplete,
    isPaymentComplete,
    selectedDeliveryMethod,
    setModalDeliveryMethod,
    setShowDeliveryModal,
    handleDeliveryModalOpen,
  ]);

  const confirmClick = async () => {
    if (readOnly) return;
    if (isPreparingConfirmation) {
      return;
    }

    setIsPreparingConfirmation(true);

    try {
      // Freeze the backend order snapshot when user starts confirmation flow.
      const orderResult = await syncOrder({
        checkoutContext,
        includeB2bContactPerson: checkoutConfig.b2bContactPerson.visible,
      });
      if (!orderResult.ok) {
        setPrepareOrderErrorMessage(orderResult.message);
        setShowPrepareOrderError(true);
        return;
      }
      if (orderResult.data?.order?.orderCompleted) {
        return;
      }

      // Collect all missing steps in order
      const missingSteps: CheckoutStep[] = [];

      if (!isBuyerComplete()) {
        missingSteps.push("buyer");
      }

      if (requiresDeliverySelection && !isDeliveryComplete()) {
        missingSteps.push("delivery");
      }

      if (requiresPaymentSelection && !isPaymentComplete()) {
        missingSteps.push("payment");
      }

      // Always add confirm as the final step
      missingSteps.push("confirm");

      // Reset processing flags
      setIsProcessingDelivery(false);
      setIsProcessingPayment(false);

      // Set pending steps - the useEffect will handle showing modals in sequence
      setPendingSteps(missingSteps);
    } finally {
      setIsPreparingConfirmation(false);
    }
  };

  // Handlers for when user explicitly cancels (X button or backdrop click)
  const handleSelectDeliveryMethodModalCancel = useCallback(() => {
    setShowSelectDeliveryMethodModal(false);
    cancelFlow();
  }, [cancelFlow]);

  const handleSelectPaymentMethodModalCancel = useCallback(() => {
    setShowSelectPaymentMethodModal(false);
    cancelFlow();
  }, [cancelFlow]);

  const handleCheckoutConfirmModalCancel = useCallback(() => {
    setShowCheckoutConfirmModal(false);
    cancelFlow();
  }, [cancelFlow]);

  // When user selects a delivery method and opens delivery details modal
  const handleDeliveryMethodSelected = useCallback(
    (deliveryMethod: DeliveryMethod) => {
      setShowSelectDeliveryMethodModal(false);
      setModalDeliveryMethod(deliveryMethod);
      setShowDeliveryModal(true);
      handleDeliveryModalOpen();
    },
    [setModalDeliveryMethod, setShowDeliveryModal, handleDeliveryModalOpen]
  );

  // Handle payment method selection - just close the modal
  // Payment is set synchronously, so no need to mark as processing
  const handlePaymentMethodSelected = useCallback(() => {
    setShowSelectPaymentMethodModal(false);
  }, []);

  const maksekeskusPaymentMethod =
    paymentMethodForDisplay?.maksekeskusPaymentMethodName
      ? paymentMethodForDisplay.apiClient?.maksekeskusPaymentMethods?.find(
          (item) =>
            item.name === paymentMethodForDisplay.maksekeskusPaymentMethodName
        ) ?? null
      : null;

  const submitButton = (
    <button
      className="btn btn-primary checkoutConfirmOrderButton shop-controlled-border-radius"
      style={{
        paddingTop: "6px",
        paddingBottom: "6px",
      }}
      disabled={isPreparingConfirmation}
      onClick={confirmClick}
    >
      {getConfirmOrderButtonText(locale, checkoutConfig)}
    </button>
  );

  const modals = (
    <>
      {showCheckoutConfirmModal && (
        <CheckoutConfirmModal
          show={showCheckoutConfirmModal}
          setShow={setShowCheckoutConfirmModal}
          onCancel={handleCheckoutConfirmModalCancel}
        />
      )}
      {showSelectDeliveryMethodModal && (
        <CheckoutSelectDeliveryMethodModal
          show={showSelectDeliveryMethodModal}
          setShow={setShowSelectDeliveryMethodModal}
          deliveryMethods={visibleDeliveryMethods}
          onDeliveryMethodSelected={handleDeliveryMethodSelected}
          onCancel={handleSelectDeliveryMethodModalCancel}
        />
      )}
      {showSelectPaymentMethodModal && (
        <CheckoutSelectPaymentMethodModal
          show={showSelectPaymentMethodModal}
          setShow={setShowSelectPaymentMethodModal}
          onPaymentMethodSelected={handlePaymentMethodSelected}
          onCancel={handleSelectPaymentMethodModalCancel}
        />
      )}
      {showPrepareOrderError && (
        <CheckoutErrorModal
          show={showPrepareOrderError}
          setShow={(show) => {
            setShowPrepareOrderError(show);
            if (!show) {
              setPrepareOrderErrorMessage(undefined);
            }
          }}
          messageKey="prepare"
          detailMessage={prepareOrderErrorMessage}
        />
      )}
      {showCheckoutErrorModal && (
        <CheckoutErrorModal
          show={showCheckoutErrorModal}
          setShow={handleCheckoutErrorModalClose}
          detailMessage={checkoutErrorMessage}
        />
      )}
    </>
  );

  return (
    <div className="panel" id="checkoutPaymentmethodPanel">
      <div className="panel-heading">
        <div className="panel-title">
          <span className="badge badge-square badge-primary ">
            {paymentStepNumber}
          </span>
          <span className="panel-title-text">
            {languageTranslations[locale]?.payment_method}
          </span>
        </div>
      </div>
      <div className="panel-body">
        {readOnly ? (
          paymentMethodForDisplay?.uuid ? (
            <div className="row paymentMethodSelected">
              <div className="col-xs-12 selectedPaymentMethodButtonConainer">
                {paymentMethodForDisplay.getMaksekeskusApiActive ? (
                  <button
                    className="btn btn-primary-outline paymentMethodMaksekeskus"
                    type="button"
                  >
                    {paymentMethodForDisplay.translations?.[locale]?.name}
                    {maksekeskusPaymentMethod ? (
                      <img
                        src={maksekeskusPaymentMethod.logoUrl}
                        style={{
                          float: "right",
                          maxWidth: "65px",
                          marginBottom: "-5px",
                        }}
                        alt=""
                      />
                    ) : (
                      ""
                    )}
                  </button>
                ) : (
                  <button
                    className="btn btn-primary-outline paymentMethodButton"
                    type="button"
                  >
                    {paymentMethodForDisplay.image ? (
                      <img
                        src={paymentMethodForDisplay.imageUrl}
                        height="25px"
                        alt=""
                      />
                    ) : (
                      <span>
                        {paymentMethodForDisplay.translations
                          ? paymentMethodForDisplay.translations[locale]?.name
                          : ""}
                      </span>
                    )}
                  </button>
                )}
              </div>
            </div>
          ) : null
        ) : showPaymentBlock ? (
          !paymentMethodForDisplay?.uuid ? (
            <div className="paymentMethodNotSelected">
              {paymentMethods
                ? paymentMethods.map((pm) => (
                    <CheckoutPaymentMethodButton
                      key={pm.uuid}
                      paymentMethod={pm}
                    />
                  ))
                : ""}
            </div>
          ) : (
            <div className="row paymentMethodSelected">
              <div className="col-xs-12">
                <button
                  className="btn btn-pure backToPaymentMethodSelect"
                  onClick={() => setPaymentMethod(null)}
                  style={{ backgroundColor: "transparent" }}
                >
                  <WestIcon className="icon" style={{ fontSize: "14px" }} />{" "}
                  <span>{languageTranslations[locale]?.back_to_payment}</span>
                </button>
              </div>
              <div className="col-xs-12 selectedPaymentMethodButtonConainer">
                <CheckoutPaymentMethodButton
                  key={paymentMethodForDisplay.uuid}
                  paymentMethod={paymentMethodForDisplay}
                />
              </div>
            </div>
          )
        ) : null}
        {!readOnly && (
          <div
            className={`col-xs-12${showPaymentBlock ? " margin-top-15" : ""}`}
            style={{ fontSize: "15px" }}
          >
            {languageTranslations[locale]?.confirm_order}
          </div>
        )}
      </div>
      {!readOnly && (
        <div className="panel-footer text-center">{submitButton}</div>
      )}
      {modals}
    </div>
  );
}
